import { useState, useEffect, useCallback, useMemo } from 'react';
import { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector, IOption } from '../../../base';
import {
    reportsActions,
    reportsSelectors,
} from '../../../actions';
import { ReportPeriod, TIME_FRAME_OPTIONS } from '../../../common';
import { IReportsFilterValue } from '../../../common/interfaces/reports.interfaces';
import { formatToYYYYMMDD } from '../../../common/utils/date.utils';
import SelectDropdown from '../../SelectDropdown';
import CustomDateRangePicker from './CustomDateRangePicker';
import { StyledFilterBar, StyledFilterItem, StyledAddFilterButton } from './ReportsFilter.styles';

interface IReportsFilterProps {
    onChange: (value: IReportsFilterValue) => void;
    defaultTimeFrame?: string;
    maxJobs?: number;
    showRecruiterFilter?: boolean;
}

const ReportsFilter = ({
    onChange,
    defaultTimeFrame,
    maxJobs = 1,
    showRecruiterFilter = true,
}: IReportsFilterProps) => {
    const isMultiSelectJobs = maxJobs > 1;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const translatedTimeFrameOptions = useMemo(
        () => TIME_FRAME_OPTIONS.map((o) => ({ ...o, label: t(o.label) })),
        [t]
    );

    const jobsOptions = useAppSelector(reportsSelectors.getJobsOptions);
    const recruitersOptions = useAppSelector(
        reportsSelectors.getRecruitersOptions
    );

    const [selectedTimeFrame, setSelectedTimeFrame] = useState<string>(
        defaultTimeFrame || ''
    );
    const [customFromDate, setCustomFromDate] = useState<Moment | null>(null);
    const [customToDate, setCustomToDate] = useState<Moment | null>(null);
    const [selectedJob, setSelectedJob] = useState<IOption | null>(null);
    const [selectedJobs, setSelectedJobs] = useState<IOption[]>([]);
    const [selectedRecruiter, setSelectedRecruiter] = useState<IOption | null>(
        null
    );

    useEffect(() => {
        dispatch(reportsActions.fetchReportFilterOptions());
    }, [dispatch]);

    const handleAddFilter = () => {
        if (!selectedTimeFrame) return;
        if (
            selectedTimeFrame === ReportPeriod.CUSTOM &&
            (!customFromDate || !customToDate)
        ) {
            return;
        }
        const value: IReportsFilterValue = {
            timeFrame: selectedTimeFrame,
        };
        if (
            selectedTimeFrame === ReportPeriod.CUSTOM &&
            customFromDate &&
            customToDate
        ) {
            value.customDateRange = {
                fromDate: formatToYYYYMMDD(
                    customFromDate.toISOString()
                ),
                toDate: formatToYYYYMMDD(customToDate.toISOString()),
            };
        }
        if (isMultiSelectJobs && selectedJobs.length > 0) {
            value.jobIds = selectedJobs.map((j) => Number(j.value));
        } else if (selectedJob) {
            value.jobId = Number(selectedJob.value);
        }
        if (selectedRecruiter) {
            value.recruiterId = Number(selectedRecruiter.value);
        }
        onChange(value);
    };

    const timeFrameOption = translatedTimeFrameOptions.find(
        (o) => o.value === selectedTimeFrame
    ) ?? null;

    const timeFrameDisplayOption: IOption | null =
        timeFrameOption?.value === ReportPeriod.CUSTOM &&
        customFromDate &&
        customToDate
            ? {
                  ...timeFrameOption,
                  label: `${customFromDate.format('DD/MM/YYYY')} - ${customToDate.format('DD/MM/YYYY')}`,
              }
            : timeFrameOption;

    const handleTimeFrameSelect = (option: IOption | null) => {
        const value = option?.value ?? '';
        setSelectedTimeFrame(value);
        if (value !== ReportPeriod.CUSTOM) {
            setCustomFromDate(null);
            setCustomToDate(null);
        }
    };

    const handleCustomRangeSelect = useCallback((from: Moment, to: Moment) => {
        setCustomFromDate(from);
        setCustomToDate(to);
    }, []);

    const renderDatePicker = useCallback(
        () => (
            <CustomDateRangePicker
                fromDate={customFromDate}
                toDate={customToDate}
                onRangeSelect={handleCustomRangeSelect}
            />
        ),
        [customFromDate, customToDate, handleCustomRangeSelect]
    );

    return (
        <StyledFilterBar container spacing={2}>
            <StyledFilterItem item xs={12} sm={6} md={3}>
                <SelectDropdown
                    options={translatedTimeFrameOptions}
                    selected={timeFrameDisplayOption}
                    onChange={handleTimeFrameSelect}
                    placeholder={t(
                        'reportsPage.filter.timeFramePlaceholder'
                    )}
                    keepOpenValues={[ReportPeriod.CUSTOM]}
                    renderExtra={renderDatePicker}
                />
            </StyledFilterItem>

            <StyledFilterItem item xs={12} sm={6} md={3}>
                <SelectDropdown
                    options={jobsOptions}
                    selected={isMultiSelectJobs ? null : selectedJob}
                    onChange={(option) => {
                        if (!isMultiSelectJobs) {
                            setSelectedJob(option);
                            if (option) setSelectedRecruiter(null);
                        }
                    }}
                    placeholder={t('reportsPage.filter.jobPlaceholder')}
                    multiSelect={isMultiSelectJobs}
                    selectedMulti={selectedJobs}
                    onMultiChange={(options) => {
                        setSelectedJobs(options);
                        if (options.length > 0) setSelectedRecruiter(null);
                    }}
                    maxSelectable={isMultiSelectJobs ? maxJobs : undefined}
                />
            </StyledFilterItem>

            {showRecruiterFilter && (
                <StyledFilterItem item xs={12} sm={6} md={3}>
                    <SelectDropdown
                        options={recruitersOptions}
                        selected={selectedRecruiter}
                        onChange={(option) => {
                            setSelectedRecruiter(option);
                            if (option) setSelectedJob(null);
                        }}
                        placeholder={t(
                            'reportsPage.filter.recruiterPlaceholder'
                        )}
                    />
                </StyledFilterItem>
            )}

            <StyledFilterItem item xs="auto">
                <StyledAddFilterButton onClick={handleAddFilter}>
                    {t('reportsPage.filter.addFilter')}
                </StyledAddFilterButton>
            </StyledFilterItem>
        </StyledFilterBar>
    );
};

export default ReportsFilter;
